/**
 * ga4-lead-events.js — A1 Compressor Warehouse
 * ----------------------------------------------------------------------------
 * Emits GA4 conversion events for sales-intent form submissions. GA4 itself is
 * already loaded sitewide (G-CS7BT19FNR) via the Google & YouTube app through
 * `content_for_header`. This file does NOT add GA4 — it only fires EVENTS.
 *
 * Events:
 *   generate_lead    PRIMARY  — 5 lead forms (import to Google Ads as conversion)
 *   sign_up          SECONDARY— newsletter (GA4 only)
 *   advisor_engaged  SECONDARY— AI advisor first message (GA4 only; called via
 *                               window.a1Track.advisorEngaged())
 *
 * Sibling of lead-attribution.js — reuses its sessionStorage['a1_attribution']
 * object. It does NOT re-discover or re-stamp forms (that is lead-attribution's
 * job); it only listens for submits + success renders to fire analytics.
 *
 * Firing model (survives the native Shopify form POST→reload):
 *   1. On submit (capture phase): stash a "pending" record in sessionStorage.
 *   2. On the next page load: if a server-rendered success element is present
 *      AND a matching pending record exists, fire the event and clear it.
 *   This guarantees exactly one event per SUCCESSFUL submission.
 */
(function () {
  'use strict';

  /* ── Keys / config ──────────────────────────────────────── */
  var ATTR_KEY      = 'a1_attribution';      // written by lead-attribution.js
  var PENDING_LEAD  = 'a1_pending_lead';
  var PENDING_SIGNUP= 'a1_pending_signup';
  var ADVISOR_FLAG  = 'a1_advisor_engaged';  // once-per-session guard
  var PENDING_TTL   = 30 * 60 * 1000;        // ignore pending records older than 30 min

  // Relative lead values for Smart Bidding (not revenue — confirm with owner).
  var LEAD_VALUES = {
    diagnostic_quote: 200,  // service visit, highest intent ($200 base diagnostic fee)
    service_quote:    150,
    product_quote:    100,
    contact_us:        50   // mixed intent
  };
  function valueFor(src) {
    if (Object.prototype.hasOwnProperty.call(LEAD_VALUES, src)) return LEAD_VALUES[src];
    if (src.indexOf('landing_') === 0) return 100;  // landing_<brand>
    return 50;
  }

  /* ── Storage helpers (never throw) ──────────────────────── */
  function readJSON(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJSON(key, obj) {
    try { sessionStorage.setItem(key, JSON.stringify(obj)); } catch (e) {}
  }
  function clearKey(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }
  function fresh(rec) {
    return rec && typeof rec.ts === 'number' && (Date.now() - rec.ts) < PENDING_TTL;
  }

  /* ── Option A: scoped gtag loader (main-context GA4 for custom events) ──
     GA4 on this store is delivered via Shopify's Customer-Events web pixel
     (sandboxed iframe), which owns pageviews + standard ecommerce events and
     does NOT forward custom events. So window.gtag is absent in the main page
     context. This loads a SECOND, scoped gtag instance in the main context for
     our custom lead events only. send_page_view:false → the pixel keeps owning
     pageviews (no double-count); this instance only sends what we explicitly
     call (generate_lead / sign_up / advisor_engaged). */
  var GA4_ID = 'G-CS7BT19FNR';
  var _gtagLoaded = false;
  var _gtagQueue  = [];

  function bootstrapGtag() {
    if (_gtagLoaded) return;
    _gtagLoaded = true;

    // Initialise dataLayer + gtag shim BEFORE the script loads.
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, {
      send_page_view:       false,  // web pixel owns pageviews — never duplicate
      allow_google_signals: false   // no remarketing audiences from this instance
    });

    // Load gtag.js async (non-blocking).
    var s = document.createElement('script');
    s.async = true;
    s.src   = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_ID + '&l=dataLayer';
    s.addEventListener('load', function () {
      _gtagQueue.forEach(function (args) { try { window.gtag.apply(window, args); } catch (e) {} });
      _gtagQueue = [];
    });
    document.head.appendChild(s);
  }

  /* ── GA4 emit — lazy-boots the scoped gtag, gates PII by consent ── */
  function emit(name, params) {
    params = params || {};
    // Consent: if marketing consent is explicitly denied, strip hashed PII but
    // STILL count the lead (never block a conversion purely on consent).
    if (!piiAllowed()) {
      delete params.em_hash;
      delete params.ph_hash;
    }
    try {
      // Lazy bootstrap on first event — no gtag.js fetch until there's
      // actually something to send.
      bootstrapGtag();
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
      } else {
        // Shouldn't happen (shim is set synchronously in bootstrap), but queue
        // defensively; flushed on gtag.js load.
        _gtagQueue.push(['event', name, params]);
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  /* ── Consent: only enrich with hashed PII when allowed ──── */
  // Spec: check Shopify Customer Privacy *if available*. If marketing consent is
  // explicitly denied, OMIT the hashes (but still fire the event). If the API is
  // not present at all, GA4 is already running sitewide, so proceed.
  function piiAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      if (typeof cp.marketingAllowed === 'function') return cp.marketingAllowed() !== false;
      if (typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed() !== false;
      return true;
    } catch (e) { return true; }
  }

  /* ── Hashing (enhanced-conversions readiness) ───────────── */
  function normEmail(v) { return String(v || '').trim().toLowerCase(); }
  function normPhone(v) {
    var d = String(v || '').replace(/[^0-9]/g, '');
    if (!d) return '';
    if (d.length === 10) return '+1' + d;          // US 10-digit → E.164
    if (d.length === 11 && d.charAt(0) === '1') return '+' + d;
    return '+' + d;                                 // already international-ish
  }
  var canHash = !!(window.crypto && window.crypto.subtle && window.TextEncoder);
  var hashCache = {};  // normalized value -> hex digest (pre-warmed while typing)

  function sha256Hex(str) {
    if (!canHash || !str) return Promise.resolve('');
    try {
      var buf = new TextEncoder().encode(str);
      return window.crypto.subtle.digest('SHA-256', buf).then(function (digest) {
        var bytes = new Uint8Array(digest), hex = '';
        for (var i = 0; i < bytes.length; i++) hex += ('0' + bytes[i].toString(16)).slice(-2);
        return hex;
      }).catch(function () { return ''; });
    } catch (e) { return Promise.resolve(''); }
  }
  function warmHash(norm) {
    if (!norm || hashCache[norm]) return;
    hashCache[norm] = '';  // mark in-flight so we don't recompute
    sha256Hex(norm).then(function (hex) { hashCache[norm] = hex; });
  }
  // Pre-compute hashes as the user types, so they're ready by submit time
  // (avoids racing an async digest against the form POST/unload).
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.name) return;
    if (t.name === 'contact[email]') warmHash(normEmail(t.value));
    else if (t.name === 'contact[phone]') warmHash(normPhone(t.value));
  }, true);

  /* ── Classify a submitted form ──────────────────────────── */
  function val(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? (el.value || '') : null;
  }
  function isNewsletter(form) {
    var tags = form.querySelector('[name="contact[tags]"]');
    if (tags && /(^|,)\s*newsletter\s*(,|$)/i.test(tags.value || '')) return true;
    return !!form.matches && form.matches('form[data-newsletter-form]');
  }
  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }
  // Returns { lead_source, brand } for a recognised lead form, else null.
  function classifyLead(form) {
    if (val(form, 'contact[type]') === 'quote_request') {
      return { lead_source: 'product_quote', brand: '' };
    }
    var src = val(form, 'contact[form_source]');
    if (src) {
      if (src === 'Contact Us page')            return { lead_source: 'contact_us',       brand: '' };
      if (src === 'Service quote')              return { lead_source: 'service_quote',    brand: '' };
      if (src === 'Diagnostic quote request')  return { lead_source: 'diagnostic_quote', brand: '' };
      var m = src.match(/^Landing\s*-\s*(.+)$/i);
      if (m) return { lead_source: 'landing_' + slug(m[1]), brand: m[1].trim() };
    }
    // Legacy landing fallback (contact[Source] = "<Brand> — Google Ads landing page")
    var legacy = val(form, 'contact[Source]');
    if (legacy && /google ads landing page/i.test(legacy)) {
      var brand = legacy.split('—')[0].split('-')[0].trim();
      return { lead_source: 'landing_' + slug(brand), brand: brand };
    }
    // Any other contact[] form that reached us — count it, but mark generic.
    return { lead_source: 'contact_form', brand: '' };
  }

  /* ── Attribution subset from lead-attribution.js ────────── */
  function attribution() {
    var a = readJSON(ATTR_KEY) || {};
    return {
      channel:  a.channel || '',
      campaign: a.utm_campaign || '',
      gclid:    a.gclid || ''
    };
  }

  /* ── On submit: stash a pending record (does NOT block POST) ── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    if (!form.querySelector('[name^="contact["]')) return;  // not a Shopify contact form

    if (isNewsletter(form)) {
      writeJSON(PENDING_SIGNUP, { ts: Date.now() });
      return;
    }

    var info = classifyLead(form);
    var attr = attribution();
    var emHash = '', phHash = '';
    var emailEl = form.querySelector('[name="contact[email]"]');
    var phoneEl = form.querySelector('[name="contact[phone]"]');
    if (canHash) {
      if (emailEl) {
        var ne = normEmail(emailEl.value);
        emHash = (ne && hashCache[ne]) || '';
        if (ne && !emHash) warmHash(ne);  // best-effort top-up before unload
      }
      if (phoneEl) {
        var np = normPhone(phoneEl.value);
        phHash = (np && hashCache[np]) || '';
        if (np && !phHash) warmHash(np);
      }
    }

    var record = {
      ts: Date.now(),
      lead_source: info.lead_source,
      brand: info.brand,
      form_id: form.id || info.lead_source,
      value: valueFor(info.lead_source),
      channel: attr.channel,
      campaign: attr.campaign,
      gclid: attr.gclid,
      em_hash: emHash,
      ph_hash: phHash
    };
    writeJSON(PENDING_LEAD, record);

    // Late top-up: if hashes weren't ready, rewrite the record once they resolve
    // (often lands before the POST round-trip completes).
    if (canHash && (!emHash || !phHash)) {
      var ne2 = emailEl ? normEmail(emailEl.value) : '';
      var np2 = phoneEl ? normPhone(phoneEl.value) : '';
      Promise.all([ne2 ? sha256Hex(ne2) : Promise.resolve(''), np2 ? sha256Hex(np2) : Promise.resolve('')])
        .then(function (h) {
          var cur = readJSON(PENDING_LEAD);
          if (cur && cur.ts === record.ts) {
            if (!cur.em_hash && h[0]) cur.em_hash = h[0];
            if (!cur.ph_hash && h[1]) cur.ph_hash = h[1];
            writeJSON(PENDING_LEAD, cur);
          }
        }).catch(function () {});
    }
  }, true);

  /* ── Success detection on (re)load ──────────────────────── */
  function leadSuccessPresent() {
    if (document.querySelector('.form-status--success')) return true;          // contact / service / diagnostic
    if (document.querySelector('.thanks.show, [data-lead-success]')) return true; // landing pages
    var badges = document.querySelectorAll('.badge--success');                  // product quote modal
    for (var i = 0; i < badges.length; i++) {
      if (!badges[i].closest || !badges[i].closest('form[data-newsletter-form]')) return true;
    }
    return false;
  }
  function newsletterSuccessPresent() {
    var f = document.querySelector('form[data-newsletter-form]');
    return !!(f && f.querySelector('.badge--success'));
  }

  function fireFromPending() {
    // Newsletter → sign_up (GA4 only)
    var signup = readJSON(PENDING_SIGNUP);
    if (signup) {
      if (fresh(signup) && newsletterSuccessPresent()) {
        emit('sign_up', { method: 'newsletter' });
      }
      clearKey(PENDING_SIGNUP);
    }

    // Lead forms → generate_lead (primary)
    var lead = readJSON(PENDING_LEAD);
    if (lead) {
      if (fresh(lead) && leadSuccessPresent()) {
        var params = {
          lead_source: lead.lead_source,
          form_id: lead.form_id,
          brand: lead.brand || '',
          value: lead.value,
          currency: 'USD',
          channel: lead.channel || '',
          campaign: lead.campaign || '',
          gclid: lead.gclid || ''
        };
        if (piiAllowed()) {
          if (lead.em_hash) params.em_hash = lead.em_hash;
          if (lead.ph_hash) params.ph_hash = lead.ph_hash;
        }
        emit('generate_lead', params);
      }
      clearKey(PENDING_LEAD);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireFromPending);
  } else {
    fireFromPending();
  }

  /* ── Public hook for the AI advisor (engagement, not a lead) ── */
  window.a1Track = window.a1Track || {};
  window.a1Track.advisorEngaged = function () {
    try {
      if (sessionStorage.getItem(ADVISOR_FLAG) === '1') return;  // once per session
      sessionStorage.setItem(ADVISOR_FLAG, '1');
    } catch (e) { /* if storage blocked, still fire once per page */ }
    emit('advisor_engaged', { engagement_type: 'ai_advisor' });
  };

})();
